import { useEffect, useState } from 'react';
import { JournalSyncRecoveryAlert } from '@/components/accounting/JournalSyncRecoveryAlert';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Activity, Calculator, BookOpen, TrendingUp, Scale, FileText,
  ArrowLeftRight, Target, Zap, Lock, Clock, Receipt,
  Repeat, Banknote, GitCompare, Gauge, ChevronRight,
  AlertCircle, Truck, Brain, Undo2,
  ShieldCheck, AlertTriangle, RefreshCw, Ban,
} from 'lucide-react';
import { useAuthStore } from '@/stores';
import { hasPermission, normalizePermissions } from '@/config/navigationConfig';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ApReconcilePanel } from '@/components/accounting/ApReconcilePanel';
import { LedgerReviewItems } from '@/components/accounting/LedgerReviewItems';
import { JournalCorrectionPreview } from '@/components/accounting/JournalCorrectionPreview';

const canSee = (item, u) => {
  if (!item.perm && !item.roles) return true;
  if (u.role === 'admin' || u.is_super_admin) return true;
  if (item.roles && !item.roles.includes(u.role)) return false;
  return hasPermission(u.permissions || [], item.perm, u.role, u.allowed_modules || []);
};

const SECTIONS = [
  {
    title: 'Dashboards',
    accent: 'bg-info',
    items: [
      { path: '/financial-dashboard', icon: Activity, label: 'Financial Dashboard', desc: 'Revenue, expenses, cash flow overview', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/financial-ratios', icon: Gauge, label: 'Financial Ratios', desc: 'Business health metrics & benchmarks', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
    ],
  },
  {
    title: 'Core Accounting',
    accent: 'bg-indigo-500',
    items: [
      { path: '/chart-of-accounts', icon: BookOpen, label: 'Chart of Accounts', desc: 'Manage accounting structure', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
      { path: '/journal-entries', icon: FileText, label: 'Journal Entries', desc: 'Debit & credit entries', color: 'text-teal-600', bg: 'bg-teal-50', ring: 'ring-teal-100' },
      { path: '/recurring-entries', icon: Repeat, label: 'Recurring Entries', desc: 'Automated periodic entries', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/auto-journal', icon: Zap, label: 'Auto Journal', desc: 'Generate entries from transactions', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
    ],
  },
  {
    title: 'Financial Reports',
    accent: 'bg-success',
    items: [
      { path: '/profit-loss', icon: TrendingUp, label: 'Profit & Loss', desc: 'Revenue vs expenses statement', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/branch-summary', icon: GitCompare, label: 'Branch Summary', desc: 'Sales by payment, COGS & expenses per branch', color: 'text-teal-600', bg: 'bg-teal-50', ring: 'ring-teal-100' },
      { path: '/aging-report', icon: Clock, label: 'Credit Aging', desc: 'Credit invoices overdue 0-30/31-60/61-90/90+ days with WhatsApp/email reminders', color: 'text-error', bg: 'bg-error/10', ring: 'ring-rose-100' },
      { path: '/balance-sheet', icon: Scale, label: 'Balance Sheet', desc: 'Assets, liabilities & equity', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/trial-balance', icon: Calculator, label: 'Trial Balance', desc: 'Verify debit/credit balance', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/cash-flow', icon: Banknote, label: 'Cash Flow', desc: 'Operating, investing, financing', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/general-ledger', icon: BookOpen, label: 'General Ledger', desc: 'All account transactions', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
      { path: '/aging-reports', icon: Clock, label: 'AR/AP Aging', desc: 'Receivable & payable aging', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/smart-dunning', icon: AlertCircle, label: 'Smart Dunning', desc: 'Auto reminders for overdue credit', color: 'text-error', bg: 'bg-error/10', ring: 'ring-rose-100' },
      { path: '/supplier-spent', icon: Truck, label: 'Supplier Spend %', desc: 'Top suppliers by share', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/ai-branch-loss', icon: Brain, label: 'AI Branch Loss', desc: 'AI-recommended mitigation', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/reversals-report', icon: Undo2, label: 'Reversals Report', desc: 'Who reversed which payments & why', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/pnl-compare', icon: GitCompare, label: 'P&L Compare', desc: 'Multi-period comparison', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/accounting/financial-audits', icon: ShieldCheck, label: 'Financial Audits', desc: 'Read-only VAT formula & missing-journal audits', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100', perm: 'accounting', roles: ['admin', 'accountant'] },
      { path: '/accounting/cancelled-transactions', icon: Ban, label: 'Cancelled Transactions', desc: 'Cancelled records, reasons & reversal journals', color: 'text-error', bg: 'bg-error/10', ring: 'ring-rose-100', perm: 'accounting', roles: ['admin', 'manager'] },
    ],
  },
  {
    title: 'Budgeting & Reconciliation',
    accent: 'bg-warning',
    items: [
      { path: '/budget-vs-actual', icon: Target, label: 'Budget vs Actual', desc: 'Track spending against budgets', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/bank-reconciliation', icon: ArrowLeftRight, label: 'Bank Reconciliation', desc: 'Match entries with bank statements', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
      { path: '/year-end-closing', icon: Lock, label: 'Year-End Closing', desc: 'Close fiscal year books', color: 'text-error', bg: 'bg-error/10', ring: 'ring-rose-100' },
    ],
  },
  {
    title: 'Transactions',
    accent: 'bg-stone-500',
    items: [
      { path: '/invoices', icon: FileText, label: 'Invoices', desc: 'Customer invoices & billing', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/bills', icon: Receipt, label: 'Bills', desc: 'Supplier bills & payments', color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
      { path: '/expenses', icon: Receipt, label: 'Expenses', desc: 'Track business expenses', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/tax-settings', icon: Receipt, label: 'Tax & Currency', desc: 'Tax rates & currency config', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
    ],
  },
];

function FeatureCard({ item, navigate }) {
  return (
    <button
      onClick={() => navigate(item.path)}
      className="group flex items-center gap-3.5 p-4 rounded-xl bg-white border border-border hover:border-border hover:shadow-md transition-all duration-200 text-left"
      data-testid={`nav-${item.path.slice(1).replace(/\//g, '-')}`}
    >
      <div className={`w-10 h-10 rounded-xl ${item.bg} ${item.color} flex items-center justify-center shrink-0 ring-1 ${item.ring} group-hover:scale-110 transition-transform duration-200`}>
        <item.icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground text-sm group-hover:text-info transition-colors">{item.label}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{item.desc}</p>
      </div>
      <ChevronRight size={14} className="text-stone-200 group-hover:text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

export default function AccountingModulePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user) || {};
  const sections = SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => canSee(i, user)) }));
  return (
    <DashboardLayout module="accounting">
      <div className="space-y-10 max-w-6xl mx-auto" data-testid="accounting-module-page">
        <JournalSyncRecoveryAlert />
        {/* Header */}
        <div className="relative">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-200/50">
              <Calculator size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Accounting</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Financial management, reporting & compliance</p>
            </div>
          </div>
        </div>

        {/* iter825 — Ledger Health */}
        <LedgerHealthCard />
        <SupplierRepaymentAuditCard />
        <Phase2CorrectionCard />
        <Dup311CorrectionCard />
        <ApReconcilePanel />

        {/* Sections */}
        {sections.map((section, si) => (
          <div key={section.title} style={{ animationDelay: `${si * 60}ms` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-1 h-5 rounded-full ${section.accent}`} />
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{section.title}</h2>
              <div className="flex-1 h-px bg-muted" />
              <span className="text-[10px] text-muted-foreground font-medium">{section.items.length} items</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item) => (
                <FeatureCard key={item.path} item={item} navigate={navigate} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}


const SAR = (n) => `SAR ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const GROUP_LINES = [
  ['payment_copies', 'Already Posted — No Action: payment-related copies (journal lives on the original payment)'],
  ['unlinked_journals', 'Journal Exists — Link Missing (no new journal needed; link repair needs approval)'],
  ['held', 'on posting hold'],
];

const AUDIT_GROUPS = [
  ['confirmed_duplicate', 'confirmed double-posted repayments'],
  ['uncertain', 'uncertain (needs bank check)'],
  ['unmatched_review', 'unmatched repayments (review)'],
  ['single_posting_ok', 'single posting (no duplicate)'],
  ['linked_ok', 'linked correctly'],
];

// Read-only supplier repayment / expense journal audit (admin). Never writes.
function SupplierRepaymentAuditCard() {
  const role = useAuthStore((s) => s.user?.role);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!['admin', 'owner', 'super_admin'].includes(role)) return null;
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get('/accounting/admin/supplier-repayment-audit'); setRes(data); }
    catch { toast.error('Audit failed'); } finally { setBusy(false); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' }));
    Object.assign(document.createElement('a'), { href: url, download: 'supplier_repayment_audit.json' }).click();
    URL.revokeObjectURL(url);
  };
  const s = res?.summary || {};
  return (
    <div className="rounded-2xl border border-border p-4 flex items-start gap-4 flex-wrap" data-testid="supplier-audit-card">
      <div className="flex-1 min-w-[240px] space-y-1">
        <div className="text-sm font-bold">Supplier repayment audit (read-only)</div>
        {res ? (
          <ul className="text-[11px] text-muted-foreground space-y-0.5" data-testid="supplier-audit-summary">
            {AUDIT_GROUPS.filter(([k]) => s[k]).map(([k, label]) => (
              <li key={k} data-testid={`supplier-audit-${k}`}>{s[k].count} {label} — {SAR(s[k].amount)}</li>
            ))}
            <li data-testid="supplier-audit-drafts">{res.pending_bank_drafts.count} pending bank drafts — {SAR(res.pending_bank_drafts.amount)}</li>
          </ul>
        ) : <p className="text-[11px] text-muted-foreground">Finds repayments posted twice to the GL. Changes nothing.</p>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={run} disabled={busy} data-testid="supplier-audit-run-btn">{busy ? 'Running…' : 'Run audit'}</Button>
        {res && <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={download} data-testid="supplier-audit-download-btn">Download full report</Button>}
      </div>
    </div>
  );
}

// Phase 2 verified duplicate-journal correction — preview is read-only; Apply stays locked until owner approval.
function Phase2CorrectionCard() {
  const role = useAuthStore((s) => s.user?.role);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!['admin', 'owner', 'super_admin'].includes(role)) return null;
  const preview = async () => {
    setBusy(true);
    try { const { data } = await api.get('/accounting/admin/phase2-corrections/preview'); setPlan(data); }
    catch (e) { toast.error(e.response?.status === 404 ? 'No correction batch for this company' : 'Preview failed'); }
    finally { setBusy(false); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(plan, null, 2)], { type: 'application/json' }));
    Object.assign(document.createElement('a'), { href: url, download: 'phase2_correction_preview.json' }).click();
    URL.revokeObjectURL(url);
  };
  const allSuppliers = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/accounting/admin/supplier-duplicate-preview');
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
      Object.assign(document.createElement('a'), { href: url, download: 'all_supplier_duplicate_preview.json' }).click();
      URL.revokeObjectURL(url);
      toast.success(`Read-only: ${data.proven_duplicates.journals} proven duplicates, ${SAR(data.proven_duplicates.amount)}`);
    } catch { toast.error('Preview failed'); } finally { setBusy(false); }
  };
  const g = plan?.groups || {};
  const applied = plan?.batch_state?.status === 'applied';
  const canApply = !!plan && plan.apply_unlocked && plan.ok && plan.approved_scope?.ok && !plan.batch_state;
  const apply = async () => {
    const phrase = window.prompt(`Type exactly: ${plan.approved_scope.confirm_phrase}`);
    if (!phrase) return;
    setBusy(true);
    try {
      const { data } = await api.post('/accounting/admin/phase2-corrections/apply', { confirm: phrase.trim() });
      toast.success(`Applied: ${data.mirrors_posted} reversal journals posted`);
      await preview();
    } catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Apply refused — see preview problems'); setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border p-4 flex items-start gap-4 flex-wrap" data-testid="phase2-correction-card">
      <div className="flex-1 min-w-[240px] space-y-1">
        <div className="text-sm font-bold">Verified duplicate journal corrections (preview)</div>
        {plan ? (
          <ul className="text-[11px] text-muted-foreground space-y-0.5" data-testid="phase2-preview-summary">
            <li data-testid="phase2-status" className={plan.ok ? 'text-emerald-600' : 'text-rose-600'}>
              {applied ? 'Applied — batch complete (permanently locked)' : plan.ok ? `Checks passed — ${plan.total_journals} journals, ${SAR(plan.total_amount)}` : `Blocked: ${plan.problems.join('; ')}`}
            </li>
            {Object.entries(g).map(([k, v]) => <li key={k} data-testid={`phase2-group-${k}`}>{k}: selected {v.selected_count} ({SAR(v.selected_amount)}) · held {v.held_count} ({SAR(v.held_amount)})</li>)}
            <li data-testid="phase2-pnl">Current-month profit change: {SAR(plan.pnl_impact_current_month?.net_profit_change)}</li>
            <li data-testid="phase2-approved-scope">{applied ? `Applied — batch complete · ${plan.apply_log_count} reversal journals posted` : `Approved batch: ${plan.approved_scope?.count} journals — ${SAR(plan.approved_scope?.amount)} ${plan.approved_scope?.ok ? '(matches approval)' : '(MISMATCH — Apply refused)'}`}</li>
            <li data-testid="phase2-md-ap">Modern Dairy AP: {SAR(plan.modern_dairy_ap_before)} → {SAR(plan.modern_dairy_ap_after)}</li>
            <li data-testid="phase2-held">Held by evidence (not applied): {plan.held_by_evidence.length} — {SAR(plan.held_amount)}</li>
            <li data-testid="phase2-md-residual">Modern Dairy remaining AP explained: {SAR(plan.modern_dairy_residual.total)} · unexplained {SAR(plan.modern_dairy_residual.unexplained)}</li>
            <li data-testid="phase2-excluded">Excluded (on hold): M4 {plan.excluded_on_hold.M4_credit_bill_journals} journals {SAR(plan.excluded_on_hold.M4_amount)}, draft {plan.excluded_on_hold.draft_10993dbf}</li>
          </ul>
        ) : <p className="text-[11px] text-muted-foreground">Shows the exact journals, kept genuine payments and before/after balances. Changes nothing.</p>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={preview} disabled={busy} data-testid="phase2-preview-btn">{busy ? 'Checking…' : 'Preview'}</Button>
        {plan && <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={download} data-testid="phase2-download-btn">Download full preview</Button>}
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={allSuppliers} disabled={busy} data-testid="all-supplier-dup-preview-btn">All-supplier duplicate preview (read-only)</Button>
        {plan && <Button size="sm" className="rounded-full text-xs" disabled={!canApply || busy} onClick={apply} data-testid="phase2-apply-btn"
          title={canApply ? 'Applies only the approved batch, once' : 'Locked'}>{applied ? 'Applied (locked)' : canApply ? `Apply ${plan.approved_scope.count} approved` : 'Apply (locked)'}</Button>}
      </div>
    </div>
  );
}

// One-time 311 duplicate batch — preview read-only; Apply only when count/amount match approval; auto-locks after success.
function Dup311CorrectionCard() {
  const role = useAuthStore((s) => s.user?.role);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!['admin', 'owner', 'super_admin'].includes(role)) return null;
  const save = (data, name) => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    Object.assign(document.createElement('a'), { href: url, download: name }).click();
    URL.revokeObjectURL(url);
  };
  const preview = async () => {
    setBusy(true);
    try { const { data } = await api.get('/accounting/admin/dup311/preview'); setPv(data); }
    catch (e) { toast.error(e.response?.status === 404 ? 'No correction batch for this company' : 'Preview failed'); }
    finally { setBusy(false); }
  };
  const bankMatch = async () => {
    setBusy(true);
    try { const { data } = await api.get('/accounting/admin/dup311/bank-match'); save(data, 'bank_payment_match.json'); toast.success(`Read-only: ${data.summary.bank_not_found} bank payments not found in statements`); }
    catch { toast.error('Bank match failed'); } finally { setBusy(false); }
  };
  const downloadJson = (obj, name) => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };
  // Owner-authorised batch CC-2026-10: preview (read-only) → typed confirmation → apply eligible items only.
  const consolidatedCorrection = async () => {
    setBusy(true);
    try {
      const { data: pv } = await api.get('/accounting/admin/consolidated-correction/preview');
      downloadJson(pv, `${pv.batch}_preview.json`);
      const names = { A: 'Held postings', B: 'VAT-held sales', C: 'Missing salary/fine/ID journals', D: 'Duplicate payment copies' };
      const lines = Object.entries(pv.summary).map(([k, v]) => `${names[k]}: reviewed ${v.reviewed} · eligible ${v.eligible} · exceptions ${v.exceptions} · already applied ${v.already_applied}`).join('\n');
      const chk = pv.approval_check;
      if (!chk.passed) { window.alert(`${pv.batch} BLOCKED — does not match your approval. Nothing will be changed.\n\n${chk.problems.join('\n')}\n\n${lines}\n\nThe full preview was downloaded.`); return; }
      const typed = window.prompt(`${pv.batch} — all safety checks PASSED (matches your approval). Preview downloaded.\n\n${lines}\n\nOnly eligible items are applied; the ${chk.found.exceptions} exceptions stay untouched. Each change is logged with before/after values.\n\nType "${pv.confirm_phrase}" to apply:`);
      if (typed !== pv.confirm_phrase) { toast.info('Not applied'); return; }
      const { data: r } = await api.post('/accounting/admin/consolidated-correction/apply', { confirm: typed });
      downloadJson(r, `${r.batch}_result.json`);
      toast.success(`Applied ${r.applied} of ${r.processed}${r.failed.length ? ` · ${r.failed.length} failed (kept unchanged)` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Correction failed');
    } finally { setBusy(false); }
  };
  // Final batch CC-2026-11: preview (read-only) → typed confirmation + preview fingerprint → runs in background.
  const finalCorrection = async () => {
    setBusy(true);
    try {
      const { data: st0 } = await api.get('/accounting/admin/cc-2026-11/status');
      if (st0.status === 'blocked') toast.warning(`Previous APPLY was blocked — nothing was changed: ${st0.reason}`);
      else if (st0.status) { downloadJson(st0, `CC-2026-11_result.json`); toast.info(`CC-2026-11 ${st0.status} — ${st0.progress ?? st0.processed ?? 0} of ${st0.planned ?? '…'} processed`); return; }
      const { data: pv } = await api.get('/accounting/admin/cc-2026-11/preview');
      if (pv.status === 'generating') { toast.info(`Preview is being prepared in the background${pv.stage ? ` (${pv.stage})` : ''}${pv.previous_error ? ` (last attempt failed: ${pv.previous_error})` : ''} — click again in a minute to download it.`); return; }
      downloadJson(pv, `${pv.batch}_preview.json`);
      const s = pv.summary.S || {};
      const phrase = 'APPLY CC-2026-11 SALES ONLY';
      const typed = window.prompt(`${pv.batch} — reviewed preview downloaded (generated ${pv.generated_at}).\n\nAPPROVED SCOPE: only the ${s.eligible} eligible sales journals (SAR ${s.eligible_amount}, VAT ${s.eligible_vat}), open period from ${pv.open_period_start}.\nON HOLD / unchanged: legacy Cash/Bank/AP/VAT transfers, payroll, supplier items (AP ${pv.supplier_position.status}).\n\nIf any approved sale changed since this preview, nothing is applied. Runs once.\n\nType "${phrase}" to apply:`);
      if (typed !== phrase) { toast.info('Not applied'); return; }
      const { data: r } = await api.post('/accounting/admin/cc-2026-11/apply', { confirm: typed, fingerprint: pv.fingerprint });
      toast.success(`${r.batch} ${r.status} — it re-checks every record, then applies. Click the button again in a few minutes to download the result.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Correction failed');
    } finally { setBusy(false); }
  };
  const systemAccounts = async () => {
    setBusy(true);
    try {
      const { data: st } = await api.get('/accounting/admin/system-accounts');
      const missing = st.accounts.filter((a) => !a.exists);
      const list = st.accounts.map((a) => `${a.name} (${a.type}, under ${a.parent_code}): ${a.exists ? `exists ${a.code}` : 'MISSING'} · ${a.held_postings} held postings${a.exists && a.held_postings ? ' (account now exists — the original hold reason is outdated; they stay held until you release them)' : ''}`).join('\n');
      if (!missing.length) { window.alert(`All approved system accounts exist:\n${list}`); return; }
      if (!window.confirm(`Create ${missing.length} missing system account(s)?\n\n${list}\n\nOnly the chart of accounts changes. Held postings stay held until released separately.`)) return;
      const { data: r } = await api.post('/accounting/admin/system-accounts/create');
      toast.success(`Created: ${r.created.map((c) => `${c.code} ${c.name}`).join(', ') || 'none'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'System account check failed');
    } finally { setBusy(false); }
  };
  const salesCatchup = async () => {
    setBusy(true);
    try {
      const { data: pv } = await api.get('/accounting/admin/sales-catchup/preview', { timeout: 300000 });
      save(pv, `sales_catchup_preview_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.json`);
      if (!pv.enabled) { window.alert(pv.reason); return; }
      const by = Object.entries(pv.by_branch_mode).map(([k, v]) => `${k}: ${v.count} · ${SAR(v.amount)}`).join('\n');
      const msg = `Sales since ${pv.since} without a journal (preview downloaded):\n\nEligible ${pv.count} · ${SAR(pv.total)} · VAT ${SAR(pv.vat)}\n${by}\nSkipped ${pv.skipped.length} (reasons in file)${pv.activated ? '\n\nDaily catch-up is already ON.' : ''}`;
      if (pv.activated || !pv.count) { window.alert(msg); return; }
      if (!window.confirm(`${msg}\n\nPost these ${pv.count} journals now and keep the daily catch-up on?`)) return;
      const { data: r } = await api.post('/accounting/admin/sales-catchup/apply', { fingerprint: pv.fingerprint, expected_count: pv.count, expected_total: pv.total }, { timeout: 300000 });
      if (r.status !== 'applied') { window.alert(`${r.reason}${r.now ? `\nApproved ${r.approved.count} · ${SAR(r.approved.total)}\nNow ${r.now.count} · ${SAR(r.now.total)}` : ''}`); return; }
      toast.success(`Posted ${r.posted} sale journals · ${r.journaled_now}/${r.approved_count} journaled · unbalanced ${r.unbalanced}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sales catch-up failed');
    } finally { setBusy(false); }
  };
  const evidencePack = async () => {
    const range = window.prompt('Period (start,end)', '2026-10-01,2026-10-20');
    if (!range) return;
    const [start, end] = range.split(',').map((x) => x.trim());
    const name = (window.prompt('Branch name — one full pack per branch (e.g. LV or RUBA)', 'LV') || '').trim().toLowerCase();
    setBusy(true);
    try {
      const { data: brs } = await api.get('/branches');
      const br = (brs.branches || brs || []).find((b) => (b.name || '').trim().toLowerCase() === name);
      if (!br) { toast.error(`Branch "${name}" not found`); return; }
      const { data } = await api.get('/accounting/admin/evidence-pack', { params: { start_date: start, end_date: end, branch_id: br?.id }, timeout: 300000 });
      save(data, `evidence_pack_${(br?.name || "all").replace(/[^A-Za-z0-9]+/g, "_")}_${start}_${end}_${data.cutoff_utc.slice(0, 16).replace(/[:T]/g, "")}.json`);
      toast.success('Evidence pack downloaded (read-only)');
    } catch { toast.error('Evidence pack failed'); } finally { setBusy(false); }
  };
  const finalVerify = async () => {
    setBusy(true);
    try {
      const end = new Date().toISOString().slice(0, 10);
      const { data } = await api.get('/accounting/admin/final-verification', { params: { start_date: `${end.slice(0, 4)}-01-01`, end_date: end }, timeout: 300000 });
      save(data, `final_verification_${end}.json`);
      toast.success('Final verification downloaded (read-only)');
    } catch { toast.error('Final verification failed'); } finally { setBusy(false); }
  };
  const apply = async () => {
    const phrase = window.prompt(`Type exactly: ${pv.confirm_phrase}`);
    if (!phrase) return;
    setBusy(true);
    try { const { data } = await api.post('/accounting/admin/dup311/apply', { confirm: phrase.trim() }); toast.success(`Applied: ${data.mirrors_posted} reversals, ${SAR(data.amount)} · skipped ${data.skipped.length}`); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : (d?.problems || [d?.detail || 'Apply refused']).join('; ')); }
    await preview();
  };
  return (
    <div className="rounded-2xl border border-border p-4 flex items-start gap-4 flex-wrap" data-testid="dup311-card">
      <div className="flex-1 min-w-[240px] space-y-1">
        <div className="text-sm font-bold">All-supplier duplicate journals — one-time batch</div>
        {pv ? (
          <ul className="text-[11px] text-muted-foreground space-y-0.5" data-testid="dup311-summary">
            <li data-testid="dup311-status" className={pv.applied || pv.matches_approval ? 'text-emerald-600' : 'text-rose-600'}>
              {pv.applied ? `Applied — batch complete · ${pv.apply_log_count} reversal journals posted (locked)` : pv.matches_approval ? 'Matches approval — ready to apply' : `Differs from approval (${pv.expected.count} / ${SAR(pv.expected.amount)}) — Apply refused, review required`}
            </li>
            <li data-testid="dup311-eligible">Eligible: {pv.eligible.count} journals — {SAR(pv.eligible.amount)}</li>
            <li data-testid="dup311-genuine">Genuine journals retained: {pv.retained_genuine_journals}</li>
            <li data-testid="dup311-skipped">Skipped by automated check: {pv.skipped.length}</li>
            <li data-testid="dup311-excluded">Excluded (not proven / held): {Object.entries(pv.excluded_not_proven).map(([k, v]) => `${k} ${v.journals}`).join(', ')} · protected {pv.protected.held_journals} journals, {pv.protected.held_payments} payments</li>
            <li data-testid="dup311-backup">Backup: {pv.applied ? 'captured' : pv.backup_ready ? 'ready (taken automatically before any write)' : 'n/a'}</li>
          </ul>
        ) : <p className="text-[11px] text-muted-foreground">Final production preview. Changes nothing.</p>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={preview} disabled={busy} data-testid="dup311-preview-btn">{busy ? 'Checking…' : 'Preview'}</Button>
        {pv && <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => save(pv, 'dup311_preview.json')} data-testid="dup311-download-btn">Download preview</Button>}
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={bankMatch} disabled={busy} data-testid="dup311-bank-match-btn">Bank match (read-only)</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={finalVerify} disabled={busy} data-testid="final-verification-btn">Final reconciliation (read-only)</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={evidencePack} disabled={busy} data-testid="evidence-pack-btn">Evidence pack (read-only)</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={systemAccounts} disabled={busy} data-testid="system-accounts-btn">System accounts</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={salesCatchup} disabled={busy} data-testid="sales-catchup-btn">Sales catch-up</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={consolidatedCorrection} disabled={busy} data-testid="consolidated-correction-btn">Correction CC-2026-10</Button>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={finalCorrection} disabled={busy} data-testid="final-correction-btn">Final correction CC-2026-11</Button>
        {pv && <Button size="sm" className="rounded-full text-xs" disabled={!pv.apply_allowed || busy} onClick={apply} data-testid="dup311-apply-btn">{pv.applied ? 'Applied (locked)' : pv.apply_allowed ? `Apply ${pv.eligible.count}` : 'Apply (locked)'}</Button>}
      </div>
    </div>
  );
}

// Ledger Health — warning is driven only by rows needing journal review.
function LedgerHealthCard() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const user = useAuthStore((s) => s.user) || {};
  const canPost = user.role === 'admin' || user.is_super_admin || normalizePermissions(user.permissions || []).accounting === 'write';

  const [branches, setBranches] = useState([]);
  const load = () => api.get('/accounting/ledger-health').then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get('/branches').then(({ data: b }) => setBranches(b.branches || b || [])).catch(() => {});
  }, []);

  const postMissing = async () => {
    setConfirmOpen(false);
    setRunning(true);
    try {
      const { data } = await api.post('/accounting/ledger-health/backfill');
      toast.success(`Posted ${data.total || 0} journal entries`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Posting failed');
    } finally {
      setRunning(false);
    }
  };

  if (!data) return null;
  const g = data.groups || {};
  const review = g.needs_review || { count: 0, amount: 0 };
  const tone = review.count === 0
    ? { box: 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20', text: 'text-emerald-700 dark:text-emerald-300', Icon: ShieldCheck }
    : data.status === 'minor'
    ? { box: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20', text: 'text-amber-700 dark:text-amber-300', Icon: AlertTriangle }
    : { box: 'border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/20', text: 'text-rose-700 dark:text-rose-300', Icon: AlertTriangle };
  const disabled = running || !data.catchup_enabled || !canPost;

  return (
    <div className={`rounded-2xl border ${tone.box} p-4 flex items-start gap-4 flex-wrap`} data-testid="ledger-health-card">
      <div className={`w-10 h-10 rounded-xl bg-white/60 dark:bg-white/10 flex items-center justify-center ${tone.text}`}><tone.Icon size={20} /></div>
      <div className="flex-1 min-w-[260px] space-y-1">
        <div className={`text-sm font-bold ${tone.text}`} data-testid="ledger-health-status">
          {review.count === 0 ? 'No transactions require journal review'
            : `${review.count} transaction${review.count === 1 ? '' : 's'} require journal review · ${SAR(review.amount)} (Journal Missing / Pending Approval)`}
        </div>
        {review.count > 0 && <p className="text-[11px] text-muted-foreground">Review each one before posting — these are not approved for automatic posting.</p>}
        <ul className="text-[11px] text-muted-foreground space-y-0.5" data-testid="ledger-health-groups">
          {GROUP_LINES.filter(([k]) => g[k]?.count).map(([k, label]) => (
            <li key={k} data-testid={`ledger-health-group-${k}`}>{g[k].count} {label}</li>
          ))}
        </ul>
        <p className="text-[10px] text-muted-foreground" data-testid="ledger-health-checked-at">
          Checked {new Date(data.checked_at).toLocaleString()} · Catch-up posting: Sales {data.catchup_scope?.sales ? 'ON' : 'OFF'} · Expenses, salaries & supplier payments {data.catchup_scope?.expenses_salary_supplier ? 'ON' : 'OFF'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={load} data-testid="ledger-health-refresh">
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
        {review.count > 0 && (
          <Button size="sm" variant="outline" className="rounded-full" disabled={disabled} onClick={() => setConfirmOpen(true)}
            title={!data.catchup_enabled ? 'Catch-up posting is OFF' : !canPost ? 'Requires accounting write permission' : ''}
            data-testid="ledger-health-backfill-btn">
            {running ? 'Posting…' : !data.catchup_enabled ? 'Catch-up posting is OFF' : data.catchup_scope?.expenses_salary_supplier ? 'Post missing journals…' : 'Post missing sales journals…'}
          </Button>
        )}
      </div>
      <LedgerReviewItems items={review.items || []} branchName={(id) => branches.find((b) => b.id === id)?.name || id || '—'} />
      <JournalCorrectionPreview branchName={(id) => branches.find((b) => b.id === id)?.name || id || '—'} />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="ledger-health-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Post missing journals?</AlertDialogTitle>
            <AlertDialogDescription>
              This is not a read-only audit. It creates journal entries for eligible transactions inside the activated catch-up window.
              {data.catchup_scope?.expenses_salary_supplier ? ' ' : ' Only SALES are covered now — it does not post salaries, expenses, supplier or bank payments. '}
              Review the correction preview first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ledger-health-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={postMissing} data-testid="ledger-health-confirm-post">Yes, post journals</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
